import AuthService from './AuthService';

/**
 * Globally shared permission groups.
 *
 * `/admin/permission-groups` — admin-only management (define/edit groups).
 * `/private/permission-groups` — the ACTIVE, directly-assignable groups shown on
 * the Create/Edit user form (admins + client owners + users who manage members).
 */
class PermissionGroupService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '';
        this.apiBaseUrl = rawBaseUrl.replace(/\/+$/, '');
        this.adminPath = '/admin/permission-groups';
        this.privatePath = '/private/permission-groups';
    }

    getAuthHeaders(extra) {
        const token = AuthService.getAccessToken();
        return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra || {}) };
    }

    async request(path, { method = 'GET', body } = {}) {
        const response = await fetch(`${this.apiBaseUrl}${path}`, {
            method,
            headers: this.getAuthHeaders(body ? { 'Content-Type': 'application/json' } : undefined),
            body: body ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            let detail = '';
            try { const p = await response.json(); detail = p?.detail ? `: ${p.detail}` : ''; } catch { detail = ''; }
            throw new Error(`HTTP error! Status: ${response.status}${detail}`);
        }
        const text = await response.text();
        if (!text) return null;
        try { return JSON.parse(text); } catch { return null; }
    }

    /** Active, directly-assignable groups (for the user form). */
    async getAssignableGroups() {
        return (await this.request(this.privatePath)) || [];
    }

    /** All groups incl. inactive (admin only). */
    async getGroups() {
        return (await this.request(this.adminPath)) || [];
    }

    /** Permission key -> description catalogue (admin only). */
    async getPermissionRegistry() {
        return (await this.request(`${this.adminPath}/permissions`)) || {};
    }

    async createGroup(payload) {
        return this.request(this.adminPath, { method: 'POST', body: payload || {} });
    }

    async updateGroup(groupId, payload) {
        if (!groupId) throw new Error('groupId is required');
        return this.request(`${this.adminPath}/${encodeURIComponent(groupId)}`, { method: 'PUT', body: payload || {} });
    }

    async deleteGroup(groupId) {
        if (!groupId) throw new Error('groupId is required');
        return this.request(`${this.adminPath}/${encodeURIComponent(groupId)}`, { method: 'DELETE' });
    }

    /** Create the system groups if missing (admin only, idempotent). */
    async seedGroups() {
        return (await this.request(`${this.adminPath}/seed`, { method: 'POST' })) || [];
    }
}

const permissionGroupService = new PermissionGroupService();
export { PermissionGroupService };
export default permissionGroupService;
